import type { SyntheticEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";

import { useMediaPreviewContext } from "./MediaPreviewContext";
import { PlayPauseOverlay, PreviewVideo, VideoContainer } from "./styledComponents";

interface IMediaPreviewVideoProps {
    src: string;
    /** Image URL used as the video poster */
    poster?: string | null;
    /** Optional "WxH" string for native width/height attributes */
    dimensions?: string | null;
    /** Called when video play event fires */
    onPlay?: () => void;
    /** Called when video pause event fires */
    onPause?: () => void;
    /** Called when video ended event fires */
    onEnded?: () => void;
    /** Called when video volume changes */
    onVolumeChange?: (event: SyntheticEvent<HTMLVideoElement>) => void;
    /** Called when media fails to load */
    onError?: () => void;
}

export const MediaPreviewVideo = ({
    src,
    poster,
    dimensions,
    onPlay,
    onPause,
    onEnded,
    onVolumeChange,
    onError,
}: IMediaPreviewVideoProps) => {
    const { state, actions } = useMediaPreviewContext();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [showOverlay, setShowOverlay] = useState(false);

    const [width, height] = dimensions?.split("x") ?? [];

    // Animate play/pause overlay
    useEffect(() => {
        setShowOverlay(true);
        const timer = setTimeout(() => {
            setShowOverlay(false);
        }, 600);
        return () => clearTimeout(timer);
    }, [state.isPlaying]);

    const handleCanPlay = () => {
        actions.setLoaded();
    };

    const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
        actions.setDuration(event.currentTarget.duration);
    };

    const handleError = () => {
        actions.setError();
        onError?.();
    };

    const handlePlay = () => {
        actions.setPlaying(true);
        onPlay?.();
    };

    const handlePause = () => {
        actions.setPlaying(false);
        onPause?.();
    };

    const handleEnded = () => {
        actions.setPlaying(false);
        onEnded?.();
    };

    return (
        <VideoContainer>
            <PreviewVideo
                data-automation="media-preview-video"
                ref={videoRef}
                src={src}
                poster={poster ?? undefined}
                width={width}
                height={height}
                muted
                playsInline
                preload="metadata"
                controls
                onLoadedMetadata={handleLoadedMetadata}
                onCanPlay={handleCanPlay}
                onError={handleError}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                onVolumeChange={onVolumeChange}
                style={{ display: state.status === "loaded" ? "block" : "none" }}
            />
            <PlayPauseOverlay $isVisible={showOverlay}>
                <SWReactIcons
                    iconName={state.isPlaying ? "pause" : "play-borderless"}
                    size="xl"
                    iconColor={colorsPalettes.carbon[0]}
                />
            </PlayPauseOverlay>
        </VideoContainer>
    );
};
